import * as React from "react";
import styled, { css } from "styled-components";
import colors from "../styles/colors";
import theme from "styled-theming"
import { datePickerBackgroundColor, ThemeType } from "../styles/theme";
import { ChangeEvent } from "shared/model/Event";
import ReactDatePicker from "react-datepicker";

const inputBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const inputBorderColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.grey_3
})

const inputPlaceholderTextColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.grey_2
})

const inputTextColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

export const inputDisabledBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark3
})

const datePickerColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
})

export const InputCss = css`
    background-color: ${inputBackgroundColor};
    border: 2px solid ${inputBorderColor};
    font-family: "Gilroy-Regular", "regular-fallback";
    font-size: var(--header-4-font-size);
    border-radius: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 0 8px 16px;
    min-height: 40px;
    color: ${inputTextColor};
    &:disabled {
        background-color: ${inputDisabledBackgroundColor};
        border-color: ${inputDisabledBackgroundColor};
    }
    &::placeholder{
        margin: 0px 10px;
        color: ${inputPlaceholderTextColor};
    }
    &:focus {
        outline: none;
    }

    &[type=number] {
        padding: 8px 11px 8px 16px;
    }
`

export const Input = styled.input<IInputProps>`
    ${InputCss}
    ${props => props.isFullWidth && css`
        width: 100%;
    `}
    ${props => props.borderRadius ? css`
        border-radius: ${props.borderRadius};
    ` : css`
        border-radius: 20px;
    `}
    ${props => props.noSideBorder && css`
        border-left: initial !important;
        border-right: initial !important;
    `}
    // Hide data list arrow
    &::-webkit-calendar-picker-indicator {
        opacity: 0;
    }
`
export const TextArea = styled.textarea`
    ${InputCss};
    background-color: ${inputBackgroundColor};
    border: 2px solid ${inputBorderColor};
    border-radius: 20px;
    padding: 8px 0 8px 16px;
    max-width: 100%;
    width: 100%;
    min-height: 40px;
    resize: vertical;
    &::placeholder{
        font-size: 1rem;
        margin: 0px 10px;
    }
    &:focus {
        outline: none;
    }
`

export const InputGroup = styled.div<{ gap?: number }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    ${props => props.gap && css`
        gap: ${props.gap}px;
    `}
    .input-group-btn {
        padding-right: 10px;
    }
    .input-group-btn.right-aligned {
        padding-right: 0px;
        padding-left: 10px;
    }
`

const SearchInputIconColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

const SearchInputCancelIcon = theme("mode", {
    [ThemeType.light]: `linear-gradient(45deg, rgba(0,0,0,0) 0%,rgba(0,0,0,0) 43%,#000 45%,#000 55%,rgba(0,0,0,0) 57%,rgba(0,0,0,0) 100%),linear-gradient(135deg, transparent 0%,transparent 43%,#000 45%,#000 55%,transparent 57%,transparent 100%);`,
    [ThemeType.dark]: `linear-gradient(45deg, rgba(255,255,255,0) 0%,rgba(255,255,255,0) 43%,${colors.white} 45%,${colors.white} 55%,rgba(255,255,255,0) 57%,rgba(255,255,255,0) 100%),linear-gradient(135deg, transparent 0%,transparent 43%,${colors.white} 45%,${colors.white} 55%,transparent 57%,transparent 100%);`
})

const SearchInputContainer = styled.div`
    display: flex;
    flex: 1;
    .search-box + i {
        font-size: var(--header-3-font-size);
        font-weight: 500;
        margin-left: -30px;
        margin-right: 12px;
        margin-top: 11px;
        visibility: hidden;
    }
    .search-box:placeholder-shown + i {
        visibility: visible;
    }
    input::-webkit-search-cancel-button {
        /* Remove default */
        -webkit-appearance: none;

        cursor: pointer;
        display: inline-block;
        width: 10px;
        height: 10px;
        border: 5px solid inherit;
        margin-right: 15px;
        background: ${SearchInputCancelIcon};
    }

    .ci-search {
        color: ${SearchInputIconColor};
    }
`

export const SearchInput: React.FC<ISearchInputProps> = (props) => {
    return (
        <SearchInputContainer>
            <Input
                type="search"
                id={`search-box-${props.id}`}
                className="search-box"
                isFullWidth={props.isFullWidth}
                defaultValue={props.defaultValue}
                value={props.value}
                onChange={props.onChange}
                placeholder={props.placeholder || FRENDS.Localizations.Search}
                disabled={props.disabled}
                style={props.style}
            />
            <i className="ci-search" />
        </SearchInputContainer>
    )
}

const PasswordInputContainer = styled.div`
    display: flex;
    flex: 1;
    align-items: center;

    input {
        padding-right: 32px;
    }

    button {
        all: unset;
        cursor: pointer;
    }

    button, div {
        margin-left: -28px;

        i {
            font-size: var(--header-4-font-size);
        }
    }
`

export const PasswordInput: React.FC<IPasswordInputSearchInputProps> = (props) => {
    const {disabled, onUnmask, ...rest} = props;

    return (
        <PasswordInputContainer>
            <Input
                disabled={disabled}
                type="password"
                {...rest}
            />

            {props.onUnmask ? (
                <button
                    disabled={disabled}
                    onClick={onUnmask}
                >
                    <i className="ci-hide" />
                </button>
            ) : (
                <div><i className="ci-hide" /></div>
            )}
        </PasswordInputContainer>
    )
}

export const DatePicker = styled(ReactDatePicker)`
    min-height: 40px;
    border: 2px solid ${colors.grey_2};
    border-radius: 20px;   
    text-indent: 15px;
    font-size: 1rem;
    font-family: "Gilroy-Regular", "regular-fallback";
    color: ${datePickerColor};
    background-color: ${datePickerBackgroundColor};
`

export interface ISearchInputProps {
    id: string;
    isFullWidth?: boolean;
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    style?: React.CSSProperties;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
}

interface IPasswordInputSearchInputProps extends React.InputHTMLAttributes<HTMLInputElement>, IInputProps {
    onUnmask?: () => void;
}

interface IInputProps {
    isFullWidth?: boolean;
    borderRadius?: string;
    noSideBorder?: boolean;
}

